import { readItems } from '../normalize';
// Catalog API (Phase 2). Products, colors, images, stock, size guides.
import { api } from './client';

export type Paginated<T> = {
  items: T[];
  page: number;
  pageSize: number;
  total: number;
  totalPages: number;
};

export type ProductListParams = {
  page?: number;
  pageSize?: number;
  search?: string;
  sort?: 'newest' | 'oldest' | 'price_asc' | 'price_desc';
  category?: string; // comma-separated category ids
  featured?: boolean;
};

function buildQuery(params: Record<string, unknown>): string {
  const sp = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== '') sp.set(k, String(v));
  });
  const s = sp.toString();
  return s ? `?${s}` : '';
}

export const productsApi = {
  // Storefront list
  list(params: ProductListParams = {}): Promise<Paginated<any>> {
    return api(`/catalog/products${buildQuery(params as Record<string, unknown>)}`);
  },
  // Storefront detail
  getBySlug(slug: string): Promise<{ product: any; colors: any[]; sizeGuide: any; related: any[] }> {
    return api(`/catalog/products/${encodeURIComponent(slug)}`);
  },
  // Home: curated new arrivals (fallback to recent)
  newArrivals(): Promise<{ items: any[] }> {
    return api('/catalog/new-arrivals');
  },
  // Admin list
  adminList(params: ProductListParams = {}): Promise<Paginated<any>> {
    return api(`/admin/products${buildQuery(params as Record<string, unknown>)}`, { auth: true });
  },
  getRelations(id: string): Promise<{ items: { main_category_id: string; subcategory_id: string | null }[] }> {
    return api(`/admin/products/${id}/relations`, { auth: true });
  },
  create(body: any): Promise<{ id: string }> {
    return api('/admin/products', { method: 'POST', body, auth: true });
  },
  update(id: string, body: any): Promise<{ ok: true }> {
    return api(`/admin/products/${id}`, { method: 'PUT', body, auth: true });
  },
  remove(id: string): Promise<{ ok: true }> {
    return api(`/admin/products/${id}`, { method: 'DELETE', auth: true });
  },
};

export const colorsApi = {
  async listForProduct(productId: string): Promise<{ items: any[] }> {
    const res = await api(`/admin/products/${productId}/colors`, { auth: true });
    return { items: readItems(res) };
  },
  create(productId: string, body: { name_en: string; name_ar?: string; hex: string }): Promise<{ ok: true }> {
    return api(`/admin/products/${productId}/colors`, { method: 'POST', body, auth: true });
  },
  update(productId: string, colorId: string, body: Partial<{ name_en: string; name_ar: string; hex: string; sort_order: number }>): Promise<{ ok: true }> {
    return api(`/admin/products/${productId}/colors/${colorId}`, { method: 'PUT', body, auth: true });
  },
  remove(productId: string, colorId: string): Promise<{ ok: true }> {
    return api(`/admin/products/${productId}/colors/${colorId}`, { method: 'DELETE', auth: true });
  },
  addImage(colorId: string, url: string): Promise<{ ok: true }> {
    return api(`/admin/colors/${colorId}/images`, { method: 'POST', body: { url }, auth: true });
  },
  removeImage(colorId: string, imageId: string): Promise<{ ok: true }> {
    return api(`/admin/colors/${colorId}/images/${imageId}`, { method: 'DELETE', auth: true });
  },
  setStock(colorId: string, size: string, stock_qty: number): Promise<{ ok: true }> {
    return api(`/admin/colors/${colorId}/stock`, { method: 'PUT', body: { size, stock_qty }, auth: true });
  },
  reorder(productId: string, items: { id: string; sort_order: number }[]): Promise<{ ok: true }> {
    return api(`/admin/products/${productId}/colors/reorder`, { method: 'PUT', body: { items }, auth: true });
  },
  reorderImages(colorId: string, items: { id: string; sort_order: number }[]): Promise<{ ok: true }> {
    return api(`/admin/colors/${colorId}/images/reorder`, { method: 'PUT', body: { items }, auth: true });
  },
};

export const sizeGuidesApi = {
  list(): Promise<{ items: any[] }> {
    return api('/admin/size-guides', { auth: true });
  },
  create(body: any): Promise<{ id: string }> {
    return api('/admin/size-guides', { method: 'POST', body, auth: true });
  },
  update(id: string, body: any): Promise<{ ok: true }> {
    return api(`/admin/size-guides/${id}`, { method: 'PUT', body, auth: true });
  },
  remove(id: string): Promise<{ ok: true }> {
    return api(`/admin/size-guides/${id}`, { method: 'DELETE', auth: true });
  },
};

export const categoriesApi = {
  // Public: active categories only (storefront)
  list(): Promise<{ items: any[] }> {
    return api('/catalog/categories');
  },
  // Admin: all categories (active + hidden)
  async adminList(): Promise<{ items: any[] }> {
    const [mains, subs] = await Promise.all([
      api<any>('/admin/categories', { auth: true }),
      api<any>('/admin/subcategories', { auth: true }),
    ]);
    return { items: [...readItems(mains), ...readItems(subs)] };
  },
  create(body: any): Promise<{ id: string }> {
    return api('/admin/categories', { method: 'POST', body, auth: true });
  },
  update(id: string, body: any): Promise<{ ok: true }> {
    return api(`/admin/categories/${id}`, { method: 'PUT', body, auth: true });
  },
  remove(id: string): Promise<{ ok: true }> {
    return api(`/admin/categories/${id}`, { method: 'DELETE', auth: true });
  },
};

export const colorPresetsApi = {
  list(): Promise<{ items: any[] }> {
    return api('/admin/color-presets', { auth: true });
  },
  create(body: { name_en: string; name_ar?: string; hex: string }): Promise<{ id: string }> {
    return api('/admin/color-presets', { method: 'POST', body, auth: true });
  },
  remove(id: string): Promise<{ ok: true }> {
    return api(`/admin/color-presets/${id}`, { method: 'DELETE', auth: true });
  },
};
